import { CommunityContext } from '@/providers/community-context';
import { useUser } from '@/hooks/use-user';
import { useCommunity } from '@/hooks/use-community';
import { DashboardShell } from './DashboardShell';
import { Loader2 } from 'lucide-react';
import { useEffect, useLayoutEffect, type CSSProperties, type ReactNode } from 'react';
import { useParams, Outlet, useLocation, Navigate } from 'react-router-dom';
import { JoinCreateView } from '@/components/dashboard/join-create-view';
import { PendingRequestView } from '@/components/dashboard/pending-request-view';
import { communityIdFromPathname } from '@/lib/community-route';
import { setLastActiveCommunityId, recordLastLeafForAncestors } from '@/lib/last-active-community';
import { getCommunityUiMode, isAggregatorKind } from '@/lib/community-ui-mode';
import { AdvertisementPopup } from '@/components/advertisements/AdvertisementPopup';
import { applyThemeColors, type ThemeColors } from '@/lib/brand-theme';
import { notifyNativeSafeAreaTheme } from '@/lib/native-safe-area-theme';
import { useTenant } from '@/providers/tenant-context';
import type { Community, UserWithCommunities } from '@/types';

interface CommunityContextType {
  community: Community;
  isLoading: boolean;
  isError: unknown;
}

function usesPendingRestrictedShell(pathname: string, communityId: string | undefined): boolean {
  if (!communityId) return false;
  const base = `/${communityId}`;
  if (pathname === `${base}/dashboard` || pathname.startsWith(`${base}/dashboard/`)) {
    return true;
  }
  return (
    pathname.startsWith(`${base}/profile`) ||
    pathname.startsWith(`${base}/create`) ||
    pathname.startsWith(`${base}/business`) ||
    pathname.startsWith(`${base}/b/`) ||
    pathname.startsWith(`${base}/ub/`)
  );
}

function isPendingDashboardPath(pathname: string, communityId: string | undefined): boolean {
  if (!communityId) return false;
  const d = `/${communityId}/dashboard`;
  return pathname === d || pathname.startsWith(`${d}/`);
}

function renderMemberShell(
  shellUser: UserWithCommunities | null | undefined,
  currentCommunity: Community,
  communities: Community[],
  children: ReactNode,
  options?: {
    showMemberNavigation?: boolean;
    membershipPendingRestricted?: boolean;
  }
) {
  return (
    <DashboardShell
      user={shellUser ?? null}
      currentCommunity={currentCommunity}
      allCommunities={communities}
      showMemberNavigation={options?.showMemberNavigation ?? true}
      membershipPendingRestricted={options?.membershipPendingRestricted ?? false}
    >
      {children}
    </DashboardShell>
  );
}

export default function CommunityLayout() {
  const location = useLocation();
  const params = useParams();
  const communityId = params.communityId ?? communityIdFromPathname(location.pathname);
  const { tenant } = useTenant();
  const { user, communities, isLoading: isUserLoading } = useUser();
  const {
    community: communityData,
    isLoading: isCommunityLoading,
    isError: isCommunityError,
  } = useCommunity(communityId);

  const currentCommunity = communityData?.community;
  const communityThemeColors = currentCommunity?.themeColors as ThemeColors | null | undefined;
  const communityPrimary = communityThemeColors?.primary?.trim() || null;
  const tenantPrimary = tenant?.themeColors?.primary?.trim() || null;

  useLayoutEffect(() => {
    if (currentCommunity) {
      applyThemeColors({ primary: communityPrimary || tenantPrimary });
      notifyNativeSafeAreaTheme();
    }
    return () => {
      applyThemeColors({ primary: tenantPrimary });
      notifyNativeSafeAreaTheme();
    };
  }, [currentCommunity, communityPrimary, tenantPrimary]);

  useEffect(() => {
    if (!user?.id || !currentCommunity?.id) return;
    const membership = communities.find((community) => community.id === currentCommunity.id) as
      | UserWithCommunities['communities'][number]
      | undefined;
    if (membership?.status !== 'JOINED') return;

    setLastActiveCommunityId(user.id, currentCommunity.id);

    if (isAggregatorKind(currentCommunity.kind)) return;

    const membershipById = new Map(communities.map((c) => [c.id, c]));
    const ancestorGroupIds: string[] = [];
    let parentId = currentCommunity.parentCommunityId ?? null;
    const seen = new Set<string>();
    while (parentId && !seen.has(parentId)) {
      seen.add(parentId);
      ancestorGroupIds.push(parentId);
      parentId = membershipById.get(parentId)?.parentCommunityId ?? null;
    }
    if (ancestorGroupIds.length > 0) {
      recordLastLeafForAncestors(user.id, currentCommunity.id, ancestorGroupIds);
    }
  }, [
    user?.id,
    currentCommunity?.id,
    currentCommunity?.kind,
    currentCommunity?.parentCommunityId,
    communities,
  ]);

  if (isUserLoading || isCommunityLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isCommunityError || !currentCommunity) {
    if (!user && !isUserLoading) {
      const from = window.location.pathname + window.location.search + window.location.hash;
      return (
        <Navigate to={`/login?redirect=${encodeURIComponent(from)}`} replace state={{ from }} />
      );
    } else {
      return <Navigate to="/" replace />;
    }
  }

  const contextValue: CommunityContextType = {
    community: currentCommunity,
    isLoading: isCommunityLoading,
    isError: isCommunityError,
  };

  const communityMember = user
    ? (communities.find((c) => c.id === currentCommunity.id) as
        | UserWithCommunities['communities'][number]
        | undefined)
    : undefined;
  const isJoinedMember = communityMember?.status === 'JOINED';
  const uiMode = getCommunityUiMode(currentCommunity);
  const showAdPopup = Boolean(
    isJoinedMember && uiMode.showAdvertisementPopup && currentCommunity.id
  );

  const renderContent = () => {
    if (!user) {
      return renderMemberShell(null, currentCommunity, [], <Outlet />);
    }

    if (!communityMember) {
      if (communities.length === 0) {
        return <JoinCreateView />;
      }
      return renderMemberShell(user, currentCommunity, communities, <Outlet />);
    }

    if (communityMember.status === 'INVITED') {
      return renderMemberShell(user, currentCommunity, communities, <Outlet />, {
        showMemberNavigation: true,
      });
    }

    if (communityMember.status === 'PENDING') {
      if (usesPendingRestrictedShell(location.pathname, communityId)) {
        return renderMemberShell(
          user,
          currentCommunity,
          communities,
          isPendingDashboardPath(location.pathname, communityId) ? (
            <PendingRequestView community={communityMember} />
          ) : (
            <Outlet />
          ),
          { showMemberNavigation: true, membershipPendingRestricted: true }
        );
      }
      return renderMemberShell(
        user,
        currentCommunity,
        communities,
        <PendingRequestView community={communityMember} />,
        { showMemberNavigation: false }
      );
    }

    return renderMemberShell(user, currentCommunity, communities, <Outlet />);
  };

  const cssVars: CSSProperties | undefined = communityThemeColors?.primary
    ? ({
        '--primary': communityThemeColors.primary,
        '--ring': communityThemeColors.primary,
      } as CSSProperties)
    : undefined;

  return (
    <div id="community-theme-root" style={cssVars}>
      <CommunityContext.Provider value={contextValue}>
        {showAdPopup && (
          <AdvertisementPopup communityId={currentCommunity.id} enabled={showAdPopup} />
        )}
        {renderContent()}
      </CommunityContext.Provider>
    </div>
  );
}
